import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody,
  TableRow, TableCell, Checkbox, Chip, Avatar, Button, Snackbar, Alert,
  TextField, InputAdornment, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, IconButton, Tabs, Tab,
} from '@mui/material';
import {
  Search, AdminPanelSettings, PersonAdd, Close, Block, CheckCircle, Edit,
  VpnKey, History, LockOpen,
} from '@mui/icons-material';
import { adminApi } from '../services/hrmsApi';
import { useAuth } from '../context/AuthContext';
import RowActionMenu from '../components/RowActionMenu';
import ConfirmActionDialog from '../components/ConfirmActionDialog';
import EditUserProfileDialog from '../components/EditUserProfileDialog';
import TempPasswordDialog from '../components/TempPasswordDialog';
import AuditLogPanel from '../components/AuditLogPanel';

const ALL_ROLES = [
  { key: 'ROLE_SUPER_USER',         label: 'Super-user',       color: '#1565C0' },
  { key: 'ROLE_ADMIN',              label: 'Admin',            color: '#6A1B9A' },
  { key: 'ROLE_WORKFORCE_PULSE',    label: 'Workforce Pulse',  color: '#00695C' },
  { key: 'ROLE_FINANCE_BUDGET',     label: 'Finance & Budget', color: '#E65100' },
  { key: 'ROLE_OPERATIONS',         label: 'Operations',       color: '#37474F' },
  { key: 'ROLE_TALENT_PERFORMANCE', label: 'Talent & Perf.',   color: '#AD1457' },
];

const TAB_LABELS = {
  'workforce-pulse':    'Tab A',
  'finance-budget':     'Tab B',
  'operations':         'Tab C',
  'talent-performance': 'Tab D',
};

const EMPTY_FORM = {
  fullName: '', username: '', email: '', department: '', password: '', roles: [],
};

export default function SettingsPage() {
  const { user: currentUser } = useAuth();

  const [users,    setUsers]    = useState([]);
  const [dataLoad, setDataLoad] = useState(true);
  const [saving,   setSaving]   = useState({});
  const [search,   setSearch]   = useState('');
  const [snack,    setSnack]    = useState({ open: false, msg: '', sev: 'success' });

  // Add-user dialog state
  const [addOpen,   setAddOpen]   = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addForm,   setAddForm]   = useState(EMPTY_FORM);
  const [addErrors, setAddErrors] = useState({});

  // Deactivate/reactivate dialog state
  const [statusTarget, setStatusTarget] = useState(null);   // { user, nextEnabled }
  const [statusSaving, setStatusSaving] = useState(false);

  // Edit-profile dialog state
  const [editTarget, setEditTarget] = useState(null);   // user

  // Reset-password flow state
  const [resetTarget, setResetTarget] = useState(null);   // user
  const [resetSaving, setResetSaving] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);   // { userName, temporaryPassword }

  // Tabs: Users | Audit Log
  const [activeTab, setActiveTab] = useState('users');
  const [auditUserFilter, setAuditUserFilter] = useState(null);

  // Unlock flow state
  const [unlockTarget, setUnlockTarget] = useState(null);   // user
  const [unlockSaving, setUnlockSaving] = useState(false);

  useEffect(() => {
    adminApi.getUsers()
      .then((list) => setUsers(list.map((u) => ({ ...u, editRoles: [...u.roles] }))))
      .catch(() => setSnack({ open: true, msg: 'Failed to load users from server.', sev: 'error' }))
      .finally(() => setDataLoad(false));
  }, []);

  const filteredUsers = users.filter(
    (u) =>
      u.fullName?.toLowerCase().includes(search.toLowerCase()) ||
      u.username?.toLowerCase().includes(search.toLowerCase()) ||
      u.department?.toLowerCase().includes(search.toLowerCase())
  );

  // ── Last-admin / self-action safeguard (defense-in-depth; backend is the real authority) ──
  const isEnabled = (u) => u.enabled !== false;   // absent field defaults to enabled (backward-compat)
  const isLocked  = (u) => u.accountLocked === true;
  const enabledSuperUserCount = users.filter((u) => isEnabled(u) && u.editRoles.includes('ROLE_SUPER_USER')).length;
  const isLastSuperUser = (u) =>
    isEnabled(u) && u.editRoles.includes('ROLE_SUPER_USER') && enabledSuperUserCount === 1;
  const isSelf = (u) => currentUser?.id != null && u.id === currentUser.id;

  // ── Existing-user role toggling ────────────────────────────────────────────

  const toggleRole = (userId, roleKey) => {
    setUsers((prev) => prev.map((u) => {
      if (u.id !== userId) return u;
      const has = u.editRoles.includes(roleKey);
      return { ...u, editRoles: has ? u.editRoles.filter((r) => r !== roleKey) : [...u.editRoles, roleKey] };
    }));
  };

  const saveUser = async (user) => {
    setSaving((s) => ({ ...s, [user.id]: true }));
    try {
      const updated = await adminApi.updateUserRoles(user.id, user.editRoles);
      setUsers((prev) => prev.map((u) =>
        u.id === user.id ? { ...updated, editRoles: [...updated.roles] } : u
      ));
      setSnack({ open: true, msg: `Roles updated for ${user.fullName}`, sev: 'success' });
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update roles.';
      setSnack({ open: true, msg, sev: 'error' });
    } finally {
      setSaving((s) => ({ ...s, [user.id]: false }));
    }
  };

  // ── Deactivate/reactivate ──────────────────────────────────────────────────

  const handleToggleStatus = async () => {
    if (!statusTarget) return;
    const { user, nextEnabled } = statusTarget;
    setStatusSaving(true);
    try {
      const updated = await adminApi.updateUserStatus(user.id, nextEnabled);
      setUsers((prev) => prev.map((u) =>
        u.id === user.id ? { ...u, ...updated, editRoles: [...(updated.roles || u.editRoles)] } : u
      ));
      setSnack({
        open: true,
        msg: `${user.fullName} ${nextEnabled ? 'reactivated' : 'deactivated'}.`,
        sev: 'success',
      });
      setStatusTarget(null);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update status.';
      setSnack({ open: true, msg, sev: 'error' });
    } finally {
      setStatusSaving(false);
    }
  };

  // ── Edit profile ───────────────────────────────────────────────────────────

  const handleProfileSaved = (updated) => {
    setUsers((prev) => prev.map((u) =>
      u.id === updated.id ? { ...u, ...updated, editRoles: [...(updated.roles || u.editRoles)] } : u
    ));
    setSnack({ open: true, msg: `Profile updated for ${updated.fullName}.`, sev: 'success' });
    setEditTarget(null);
  };

  // ── Reset password ─────────────────────────────────────────────────────────

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setResetSaving(true);
    try {
      const { temporaryPassword } = await adminApi.resetUserPassword(resetTarget.id);
      setTempPassword({ userName: resetTarget.fullName, temporaryPassword });
      setResetTarget(null);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to reset password.';
      setSnack({ open: true, msg, sev: 'error' });
    } finally {
      setResetSaving(false);
    }
  };

  // ── Unlock account ─────────────────────────────────────────────────────────

  const handleUnlock = async () => {
    if (!unlockTarget) return;
    setUnlockSaving(true);
    try {
      const updated = await adminApi.unlockUser(unlockTarget.id);
      setUsers((prev) => prev.map((u) =>
        u.id === unlockTarget.id ? { ...u, ...updated, editRoles: [...(updated.roles || u.editRoles)] } : u
      ));
      setSnack({ open: true, msg: `${unlockTarget.fullName} unlocked.`, sev: 'success' });
      setUnlockTarget(null);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to unlock account.';
      setSnack({ open: true, msg, sev: 'error' });
    } finally {
      setUnlockSaving(false);
    }
  };

  // ── Add-user dialog handlers ───────────────────────────────────────────────

  const openAddDialog = () => {
    setAddForm(EMPTY_FORM);
    setAddErrors({});
    setAddOpen(true);
  };

  const closeAddDialog = () => {
    if (!addSaving) setAddOpen(false);
  };

  const setField = (key) => (e) =>
    setAddForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleAddRole = (roleKey) =>
    setAddForm((f) => ({
      ...f,
      roles: f.roles.includes(roleKey)
        ? f.roles.filter((r) => r !== roleKey)
        : [...f.roles, roleKey],
    }));

  const validateAdd = () => {
    const errs = {};
    if (!addForm.fullName.trim())  errs.fullName  = 'Required';
    if (!addForm.username.trim())  errs.username  = 'Required';
    if (!addForm.email.trim())     errs.email     = 'Required';
    if (!addForm.password.trim())  errs.password  = 'Required';
    else if (addForm.password.length < 6) errs.password = 'Min 6 characters';
    return errs;
  };

  const submitAdd = async () => {
    const errs = validateAdd();
    if (Object.keys(errs).length > 0) { setAddErrors(errs); return; }

    setAddSaving(true);
    try {
      const created = await adminApi.createUser(addForm);
      setUsers((prev) =>
        [...prev, { ...created, editRoles: [...created.roles] }]
          .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''))
      );
      setSnack({ open: true, msg: `User "${created.fullName}" created successfully.`, sev: 'success' });
      setAddOpen(false);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create user.';
      setSnack({ open: true, msg, sev: 'error' });
    } finally {
      setAddSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (dataLoad) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: '12px', bgcolor: '#6A1B9A14',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <AdminPanelSettings sx={{ color: '#6A1B9A', fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>User Access Control</Typography>
          <Typography variant="body2" color="text.secondary">Role changes sync to Spring Boot API on save.</Typography>
        </Box>
        {activeTab === 'users' && (
          <Button variant="contained" startIcon={<PersonAdd />} onClick={openAddDialog}
            sx={{ whiteSpace: 'nowrap' }}>
            Add User
          </Button>
        )}
      </Box>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab value="users" label="Users" />
        <Tab value="audit" label="Audit Log" />
      </Tabs>

      {activeTab === 'audit' && (
        <AuditLogPanel users={users} initialUserId={auditUserFilter} />
      )}

      {activeTab === 'users' && (
      <>
      {/* Role legend */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={700} mb={1.5}>ROLE DEFINITIONS</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {ALL_ROLES.map((r) => (
              <Chip key={r.key} label={r.label} size="small"
                sx={{ bgcolor: r.color, color: '#fff', fontWeight: 700 }} />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Search */}
      <TextField
        size="small" placeholder="Search by name, username or department…"
        value={search} onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment> }}
        sx={{ mb: 2, width: { xs: '100%', sm: 380 } }}
      />

      {/* Users table */}
      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFF' }}>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>USER</TableCell>
                {ALL_ROLES.map((r) => (
                  <TableCell key={r.key} align="center"
                    sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.72rem', minWidth: 80 }}>
                    {r.label}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>TAB ACCESS</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>STATUS</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>LAST LOGIN</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>ACTION</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id} sx={{ '&:hover': { bgcolor: '#F8FAFF' } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: '#1565C0', fontSize: '0.8rem', fontWeight: 700 }}>
                        {user.avatarInitials}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>{user.fullName}</Typography>
                        <Typography variant="caption" color="text.secondary">@{user.username} · {user.department}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  {ALL_ROLES.map((role) => (
                    <TableCell key={role.key} align="center">
                      <Checkbox size="small"
                        checked={user.editRoles.includes(role.key)}
                        onChange={() => toggleRole(user.id, role.key)}
                        sx={{ color: role.color, '&.Mui-checked': { color: role.color } }}
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    {user.editRoles.includes('ROLE_SUPER_USER') ? (
                      <Chip label="All Tabs" size="small"
                        sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 700 }} />
                    ) : (user.accessibleTabs || []).length === 0 ? (
                      <Chip
                        label={user.editRoles.includes('ROLE_ADMIN') ? 'Settings Only' : 'No Access'}
                        size="small"
                        sx={{ bgcolor: '#EDE7F6', color: '#6A1B9A', fontWeight: 700 }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(user.accessibleTabs || []).map((t) => (
                          <Chip key={t} label={TAB_LABELS[t] || t} size="small"
                            sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, fontSize: '0.68rem' }} />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={isLocked(user) ? 'Locked' : isEnabled(user) ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        fontWeight: 700, fontSize: '0.7rem',
                        bgcolor: isLocked(user) ? '#FFEBEE' : isEnabled(user) ? '#E8F5E9' : '#F5F5F5',
                        color:   isLocked(user) ? '#C62828' : isEnabled(user) ? '#2E7D32' : '#757575',
                      }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })
                        : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Button size="small" variant="contained"
                        onClick={() => saveUser(user)}
                        disabled={!!saving[user.id]}
                        sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5, minWidth: 72 }}>
                        {saving[user.id] ? <CircularProgress size={14} color="inherit" /> : 'Save'}
                      </Button>
                      <RowActionMenu items={[
                        {
                          key: 'edit-profile',
                          label: 'Edit Profile',
                          icon: <Edit fontSize="small" />,
                          onClick: () => setEditTarget(user),
                        },
                        {
                          key: 'reset-password',
                          label: 'Reset Password',
                          icon: <VpnKey fontSize="small" />,
                          disabled: isSelf(user),
                          tooltip: isSelf(user) ? 'Use "Change Password" in your account menu instead' : undefined,
                          onClick: () => setResetTarget(user),
                        },
                        {
                          key: 'view-audit-log',
                          label: 'View Audit Log',
                          icon: <History fontSize="small" />,
                          onClick: () => { setAuditUserFilter(user.id); setActiveTab('audit'); },
                        },
                        {
                          key: 'unlock',
                          label: 'Unlock Account',
                          icon: <LockOpen fontSize="small" />,
                          hidden: !isLocked(user),
                          onClick: () => setUnlockTarget(user),
                        },
                        isEnabled(user) ? {
                          key: 'deactivate',
                          label: 'Deactivate',
                          icon: <Block fontSize="small" />,
                          color: 'error.main',
                          disabled: isSelf(user) || isLastSuperUser(user),
                          tooltip: isSelf(user)
                            ? 'You cannot deactivate your own account'
                            : isLastSuperUser(user)
                              ? 'Cannot deactivate the last Super-user'
                              : undefined,
                          onClick: () => setStatusTarget({ user, nextEnabled: false }),
                        } : {
                          key: 'reactivate',
                          label: 'Reactivate',
                          icon: <CheckCircle fontSize="small" />,
                          onClick: () => setStatusTarget({ user, nextEnabled: true }),
                        },
                      ]} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </>
      )}

      {/* Add User dialog */}
      <Dialog open={addOpen} onClose={closeAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAdd sx={{ color: '#6A1B9A' }} />
            <Typography variant="h6" fontWeight={700}>Add New User</Typography>
          </Box>
          <IconButton onClick={closeAddDialog} disabled={addSaving} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Full Name *" size="small" fullWidth
              value={addForm.fullName} onChange={setField('fullName')}
              error={!!addErrors.fullName} helperText={addErrors.fullName}
            />
            <TextField
              label="Username *" size="small" fullWidth
              value={addForm.username} onChange={setField('username')}
              error={!!addErrors.username} helperText={addErrors.username}
            />
            <TextField
              label="Email *" type="email" size="small" fullWidth
              value={addForm.email} onChange={setField('email')}
              error={!!addErrors.email} helperText={addErrors.email}
            />
            <TextField
              label="Department" size="small" fullWidth
              value={addForm.department} onChange={setField('department')}
            />
            <TextField
              label="Password *" type="password" size="small" fullWidth
              value={addForm.password} onChange={setField('password')}
              error={!!addErrors.password} helperText={addErrors.password}
            />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={700} mb={1}>
                ASSIGN ROLES
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {ALL_ROLES.map((r) => (
                  <FormControlLabel
                    key={r.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={addForm.roles.includes(r.key)}
                        onChange={() => toggleAddRole(r.key)}
                        sx={{ color: r.color, '&.Mui-checked': { color: r.color } }}
                      />
                    }
                    label={<Typography variant="body2">{r.label}</Typography>}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={closeAddDialog} disabled={addSaving}>Cancel</Button>
          <Button variant="contained" onClick={submitAdd} disabled={addSaving}
            startIcon={addSaving ? <CircularProgress size={14} color="inherit" /> : <PersonAdd />}>
            {addSaving ? 'Creating…' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit profile */}
      <EditUserProfileDialog
        open={!!editTarget}
        user={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={handleProfileSaved}
      />

      {/* Reset password */}
      <ConfirmActionDialog
        open={!!resetTarget}
        title="Reset Password"
        body={
          <>This immediately invalidates <strong>{resetTarget?.fullName}</strong>'s current
          password and issues a new temporary one. They will be required to set a new
          password on their next login. Continue?</>
        }
        confirmLabel="Reset Password"
        loading={resetSaving}
        onConfirm={handleResetPassword}
        onClose={() => setResetTarget(null)}
      />
      <TempPasswordDialog
        open={!!tempPassword}
        userName={tempPassword?.userName}
        temporaryPassword={tempPassword?.temporaryPassword}
        onClose={() => setTempPassword(null)}
      />

      {/* Unlock account */}
      <ConfirmActionDialog
        open={!!unlockTarget}
        title="Unlock Account"
        body={<>Unlock <strong>{unlockTarget?.fullName}</strong>'s account? Their failed login count will be reset and they will be able to sign in again.</>}
        confirmLabel="Unlock"
        confirmColor="primary"
        loading={unlockSaving}
        onConfirm={handleUnlock}
        onClose={() => setUnlockTarget(null)}
      />

      {/* Deactivate/reactivate confirmation */}
      <ConfirmActionDialog
        open={!!statusTarget}
        title={statusTarget?.nextEnabled ? 'Reactivate User' : 'Deactivate User'}
        body={
          statusTarget?.nextEnabled
            ? <>Reactivate <strong>{statusTarget?.user.fullName}</strong>? They will regain login access immediately.</>
            : <>Deactivate <strong>{statusTarget?.user.fullName}</strong>? They will be immediately unable to log in. This does not delete their account or history.</>
        }
        confirmLabel={statusTarget?.nextEnabled ? 'Reactivate' : 'Deactivate'}
        confirmColor={statusTarget?.nextEnabled ? 'primary' : 'error'}
        loading={statusSaving}
        onConfirm={handleToggleStatus}
        onClose={() => setStatusTarget(null)}
      />

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.sev} variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
